import { clone, flatten, map, mapObjIndexed, path, pathOr, prop } from 'ramda'
import { createSelector } from 'reselect'

import { SENDER_ID_TYPES } from '@/app/module/campaigns/definitions'
import { processAudio, processText } from '@/app/module/campaigns/language-helpers'
import {
  processActionsInWhatsappButtons,
  processActionsInWhatsappList,
} from '@/app/module/campaigns/utils/actions/helpers'
import {
  processActionsInLists,
  processActionsInObjects,
  processOpenEndedActions,
} from '@/app/module/campaigns/utils/process-actions'
import { selectApiPersonalizationList, selectPersonalizationList } from '@/app/module/logs/store/selectors'
import { selectUserTimezone } from '@/app/module/user/store/selectors'

export const selectHeaders = () => [
  {
    title: 'Name',
    fields: ['name'],
  },
  {
    title: 'Type',
    fields: ['type'],
  },
  {
    title: 'Created',
    fields: ['createdAt'],
  },
  {
    title: 'Launch Time',
    fields: ['launched'],
  },
  {
    title: 'Status',
    fields: ['state'],
  },
  {
    title: 'Subscriptions',
    fields: ['runs'],
  },
]

export const selectCampaignItemConfig = createSelector(selectUserTimezone, (tz) => ({
  id: {
    type: 'hidden',
    display: 'hidden',
  },
  name: {
    type: 'text',
    display: 'text',
  },
  type: {
    type: 'type',
    display: 'type',
  },
  createdAt: {
    type: 'datetime',
    display: 'datetime',
    tz,
  },
  launched: {
    type: 'text',
    display: 'text',
  },
  state: {
    type: 'state',
    display: 'state',
  },
  runs: {
    type: 'runs',
    display: 'runs',
  },
}))

const partSelectData = path(['logs', 'data'])

const partSelectSenderIds = createSelector(partSelectData, pathOr([], ['senderIds']))

export const selectSMSSenderIdValues = createSelector(partSelectSenderIds, (senderIds) => [
  ...map(
    ({ phonenumber, sender, name }) => ({
      label: name ? `${name} (${sender || phonenumber})` : sender || phonenumber,
      value: sender || phonenumber,
      type: sender ? 'sender' : 'phonenumber',
    }),
    pathOr([], ['sms'], senderIds),
  ),
])

export const selectWhatsappSenderIds = createSelector(partSelectSenderIds, (senderIds) => [
  ...map(
    ({ phonenumber, sender, name }) => ({
      label: name ? `${name} (${sender || phonenumber})` : sender || phonenumber,
      value: sender || phonenumber,
      type: sender ? 'sender' : 'phonenumber',
    }),
    pathOr([], ['whatsapp'], senderIds),
  ),
])

export const selectSenderIdValuesForDrip = createSelector(partSelectSenderIds, (senderIds) => senderIds)

export const selectSenderIdsByType = ({ messageType, newMessages, type }) =>
  flatten(
    map(
      (src) =>
        src.listName !== 'defaultSenderId'
          ? map(
              (types) =>
                pathOr([], ['senderIdsForDrip', messageType.name], newMessages)
                  .filter(prop(types.key))
                  .map(({ [types.key]: senderIdVal, name }) => ({
                    label: name ? `${name} (${senderIdVal})` : senderIdVal,
                    value: senderIdVal,
                  })),
              src.types,
            )
          : [...pathOr([], ['defaultSenderId'], newMessages)],
      pathOr([], [type, 'dataSource'], SENDER_ID_TYPES),
    ),
  )

export const selectManageCampaignContactConfig = () => ({
  name: {
    type: 'text',
    display: 'text',
  },
  fullPhoneNumber: {
    type: 'phone',
    display: 'phone',
  },
  subscription: {
    type: 'runs',
    display: 'runs',
  },
})

export const selectGroupsHeaders = () => [
  {
    title: 'Name',
    fields: ['name'],
  },
  {
    title: 'Number of contacts',
    fields: ['numberOfContacts'],
  },
]

export const selectSegmentsHeaders = selectGroupsHeaders

export const checkIfCallerIsValid = (item) => {
  if (
    pathOr([], ['variables', 'callerIds'], item).length > 0 ||
    typeof path(['variables', 'callerId'], item) === 'string'
  ) {
    return true
  }
  return false
}

// To handle old operands. Maybe one day we will remove it.
export const processConditions = (conditions) => {
  let stringified = JSON.stringify(conditions)
  stringified = stringified.replace(/\["notEmpty",/g, '["not_empty",')
  stringified = stringified.replace(/\["contains",/g, '["contain",')
  stringified = stringified.replace(/\["notContains",/g, '["not_contain",')
  return JSON.parse(stringified)
}

export const processActionsMap = {
  multidrip: {
    key: 'parts',
    questions: ['smsMessage', 'smsQuestion', 'voiceMessage', 'voiceQuestion', 'action', 'whatsappQuestion'],
    smsMessage: {
      message: processText,
    },
    smsQuestion: {
      message: processText,
      onAnswer: processActionsInLists,
      onInvalidReply: processActionsInObjects,
      onTimeout: processActionsInObjects,
      onRetriesExhausted: processActionsInObjects,
      openEnded: processOpenEndedActions,
    },
    voiceMessage: {
      audio: processAudio,
    },
    voiceQuestion: {
      audio: processAudio,
      onAnswer: processActionsInLists,
      onInvalidReply: processActionsInObjects,
      onTimeout: processActionsInObjects,
      onRetriesExhausted: processActionsInObjects,
      spoken: processActionsInObjects,
      callResultActions: processActionsInLists,
      reconnectActions: processActionsInLists,
    },
    whatsappQuestion: {
      onAnswer: processActionsInLists,
      onButtons: processActionsInWhatsappButtons,
      onInvalidReply: processActionsInObjects,
      onList: processActionsInWhatsappList,
      onRetriesExhausted: processActionsInObjects,
      onTimeout: processActionsInObjects,
      openEnded: processOpenEndedActions,
    },
    action: {
      actionsOnly: processActionsInObjects,
    },
    topup: {
      topup: processActionsInObjects,
    },
  },
  ivr: {
    key: 'parts',
    questions: ['voiceMessage', 'voiceQuestion'],
    voiceMessage: {
      audio: processAudio,
    },
    voiceQuestion: {
      audio: processAudio,
      onAnswer: processActionsInLists,
      onInvalidReply: processActionsInObjects,
      onTimeout: processActionsInObjects,
      onRetriesExhausted: processActionsInObjects,
      spoken: processActionsInObjects,
      callResultActions: processActionsInLists,
      reconnectActions: processActionsInLists,
    },
  },
  smssurvey: {
    key: 'parts',
    questions: ['smsMessage', 'smsQuestion'],
    smsMessage: {
      message: processText,
    },
    smsQuestion: {
      message: processText,
      onAnswer: processActionsInLists,
      onInvalidReply: processActionsInObjects,
      onTimeout: processActionsInObjects,
      onRetriesExhausted: processActionsInObjects,
      openEnded: processOpenEndedActions,
    },
  },
  whatsappsurvey: {
    key: 'parts',
    questions: ['whatsappQuestion'],
    whatsappQuestion: {
      onAnswer: processActionsInLists,
      onButtons: processActionsInWhatsappButtons,
      onInvalidReply: processActionsInObjects,
      onList: processActionsInWhatsappList,
      onRetriesExhausted: processActionsInObjects,
      onTimeout: processActionsInObjects,
      openEnded: processOpenEndedActions,
    },
  },
  cati: {
    key: 'parts',
    questions: ['catiMessage', 'catiQuestion'],
    catiMessage: {
      message: processText,
    },
    catiQuestion: {
      message: processText,
      onAnswer: processActionsInLists,
      openEnded: processOpenEndedActions,
    },
  },
}

export const processVariables = (item, processFn) => {
  const { template } = item
  const { senderId, senderIdQuestions, senderIdWhatsapp } = pathOr({}, ['variables'], item)
  const newVariables = pathOr({}, ['variables'], item)
  if (template === 'ivr') {
    const { reconnectActions = [] } = newVariables
    newVariables.reconnectActions = processActionsInLists(
      reconnectActions,
      { senderId, senderIdQuestions, senderIdWhatsapp },
      processFn,
    )
  }
  const parts = pathOr([], ['variables', 'parts'], item).map((part) => {
    const newPart = clone(part)
    mapObjIndexed((val, question) => {
      if (processActionsMap[template].questions.includes(question)) {
        mapObjIndexed((actionVal, key) => {
          const processActions = path([template, question, key], processActionsMap)
          if (processActions && key !== 'message' && key !== 'audio') {
            const cleansed = processActions(actionVal, { senderId, senderIdQuestions, senderIdWhatsapp }, processFn)
            newPart[question][key] = cleansed
          }
        }, val)
      } else if (question === 'actionsOnly') {
        const cleansed = processActionsInObjects(val, { senderId, senderIdQuestions, senderIdWhatsapp }, processFn)
        newPart[question] = cleansed
      } else if (question === 'quotaActions') {
        const onExhausted = processActionsInObjects(
          { actions: val.onExhausted },
          { senderId, senderIdQuestions, senderIdWhatsapp },
          processFn,
        )
        const onOpen = processActionsInObjects(
          { actions: val.onOpen },
          { senderId, senderIdQuestions, senderIdWhatsapp },
          processFn,
        )
        newPart[question] = {
          onExhausted: onExhausted.actions || [],
          onOpen: onOpen.actions || [],
        }
      } else if (val && question === 'topup' && val.onSuccess) {
        const { onSuccess, onError } = val
        newPart[question] = {
          ...newPart[question],
          onSuccess,
          onError,
        }
      } else if (question === 'callResultActions') {
        const cleansed = processActionsInLists(val, { senderId, senderIdQuestions, senderIdWhatsapp }, processFn)
        newPart[question] = cleansed
      }
    }, newPart)
    return newPart
  })
  return {
    ...newVariables,
    parts,
  }
}

const selectCustomFields = path(['customFields'])
const selectLogs = path(['logs'])

export const selectMessagesState = createSelector(selectCustomFields, selectLogs, (customFields, logs) => ({
  loading: pathOr(false, ['loading'], logs),
  error: pathOr({}, ['error'], logs),
  senderIds: selectSMSSenderIdValues({ logs }),
  senderIdsForDrip: selectSenderIdValuesForDrip({ logs }),
  whatsappSenderIds: selectWhatsappSenderIds({ logs }),
  personalizationList: selectPersonalizationList({ customFields }),
  apiPersonalizationList: selectApiPersonalizationList({ customFields }),
}))
